import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Star, ArrowUp, ArrowDown } from "lucide-react";

/**
 * VariantManager
 * Inline editor for a product's `variants[]` array.
 * Fields per variant: id, label, price, sku, inventory, default, active, sort_order.
 */
export default function VariantManager({ value, onChange }) {
  const variants = Array.isArray(value) ? value : [];
  const [error, setError] = useState("");

  const update = (idx, patch) => {
    const next = variants.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    onChange(next);
  };

  const addVariant = () => {
    setError("");
    const next = [
      ...variants,
      {
        // Frontend-only tempId; backend will assign the real id on save.
        id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        label: "",
        price: 0,
        sku: "",
        inventory: 0,
        default: variants.length === 0,
        active: true,
        sort_order: variants.length,
      },
    ];
    onChange(next);
  };

  const removeVariant = (idx) => {
    const removed = variants[idx];
    const next = variants.filter((_, i) => i !== idx);
    // If we removed the default, promote the first remaining active variant.
    if (removed?.default && next.length > 0) {
      const promoteIdx = next.findIndex((v) => v.active !== false);
      if (promoteIdx >= 0) next[promoteIdx] = { ...next[promoteIdx], default: true };
    }
    onChange(next);
  };

  const setDefault = (idx) => {
    const next = variants.map((v, i) => ({ ...v, default: i === idx }));
    onChange(next);
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= variants.length) return;
    const next = variants.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((v, i) => { v.sort_order = i; });
    onChange(next);
  };

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid="variant-manager">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Dose Variants</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Add one row per dose (e.g. 5mg, 10mg). One variant must be flagged as default. Inactive variants are hidden from the shop but preserved for order history.
          </div>
        </div>
        <Button type="button" onClick={addVariant} size="sm" data-testid="add-variant-btn"
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-md h-8 text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2}/> Add variant
        </Button>
      </div>

      {variants.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-500 border border-dashed border-slate-300 rounded-md">
          No variants yet. Click "Add variant" to define at least one dose.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[24px_1fr_100px_120px_90px_90px_100px] gap-2 px-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
            <div></div>
            <div>Label</div>
            <div className="text-right">Price</div>
            <div>SKU</div>
            <div className="text-right">Stock</div>
            <div className="text-center">Active</div>
            <div className="text-right">Actions</div>
          </div>
          {variants.map((v, idx) => (
            <div
              key={v.id || idx}
              data-testid={`variant-row-${idx}`}
              className={`grid grid-cols-[24px_1fr_100px_120px_90px_90px_100px] gap-2 items-center bg-white border rounded-md p-2 ${v.default ? "border-blue-400" : "border-slate-200"}`}
            >
              <button
                type="button"
                data-testid={`variant-default-${idx}`}
                onClick={() => setDefault(idx)}
                title="Set as default"
                className={`w-6 h-6 rounded flex items-center justify-center ${v.default ? "text-blue-600" : "text-slate-300 hover:text-slate-500"}`}
              >
                <Star className="w-4 h-4" fill={v.default ? "currentColor" : "none"} strokeWidth={1.75}/>
              </button>
              <Input
                data-testid={`variant-label-${idx}`}
                required
                placeholder="e.g. 5mg"
                value={v.label || ""}
                onChange={(e) => update(idx, { label: e.target.value })}
                className="h-9 text-sm"
              />
              <Input
                data-testid={`variant-price-${idx}`}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={v.price ?? 0}
                onChange={(e) => update(idx, { price: Number(e.target.value) })}
                className="h-9 text-sm text-right font-mono"
              />
              <Input
                data-testid={`variant-sku-${idx}`}
                placeholder="SKU"
                value={v.sku || ""}
                onChange={(e) => update(idx, { sku: e.target.value })}
                className="h-9 text-sm font-mono"
              />
              <Input
                data-testid={`variant-inventory-${idx}`}
                type="number"
                min="0"
                placeholder="0"
                value={v.inventory ?? 0}
                onChange={(e) => update(idx, { inventory: Number(e.target.value) })}
                className="h-9 text-sm text-right font-mono"
              />
              <div className="flex items-center justify-center">
                <input
                  data-testid={`variant-active-${idx}`}
                  type="checkbox"
                  checked={v.active !== false}
                  onChange={(e) => update(idx, { active: e.target.checked })}
                />
              </div>
              <div className="flex items-center justify-end gap-0.5">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                  title="Move up"
                >
                  <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.75}/>
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === variants.length - 1}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                  title="Move down"
                >
                  <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.75}/>
                </button>
                <button
                  type="button"
                  data-testid={`variant-remove-${idx}`}
                  onClick={() => removeVariant(idx)}
                  className="p-1 rounded hover:bg-red-50 text-red-500"
                  title="Remove variant (soft-deleted; preserved for order history)"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
}
